/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Database, GitCommitHorizontal, Package, Wrench } from 'lucide-react';
import { useRef } from 'react';
import { AppBrandV2 } from '@/components/layout/AppBrand';
import StaticMarkdown from '@/components/charts/StaticMarkdown';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import {
    APPLICATION_CHANGELOG,
    APPLICATION_VERSION,
    APPLICATION_VERSION_MODIFIED_AT,
    CONTACT_DETAILS,
    IMPORTANT_VERSION_ENTRIES,
} from '@/config/footerContent';
import { IS_DEMO_USER } from '@/config/csvSensors';
import { OPEN_DATA_LICENSE_ENTRIES } from '@/config/openDataLicenses';

interface ChangelogVersion {
    heading: string;
    value: string;
}

function getChangelogVersions(content: string): ChangelogVersion[] {
    return Array.from(content.matchAll(/^## (\[\d+\.\d+\.\d+\] - \d{4}-\d{2}-\d{2})$/gm)).map((match) => ({
        heading: match[1],
        value: match[1],
    }));
}

interface VersionsTabProps {
    hasSession: boolean;
}

function VersionsTab({ hasSession }: VersionsTabProps) {
    const { t } = useI18n();
    const changelogContainerRef = useRef<HTMLDivElement>(null);
    const changelogVersions = getChangelogVersions(APPLICATION_CHANGELOG);

    function scrollToChangelogVersion(heading: string): void {
        const changelogContainer = changelogContainerRef.current;

        if (!changelogContainer) {
            return;
        }

        const targetHeading = Array.from(changelogContainer.querySelectorAll('h2')).find((element) => {
            return element.textContent?.trim() === heading;
        });

        if (!targetHeading) {
            return;
        }

        const containerBounds = changelogContainer.getBoundingClientRect();
        const targetBounds = targetHeading.getBoundingClientRect();

        changelogContainer.scrollTo({
            behavior: 'smooth',
            top: changelogContainer.scrollTop + targetBounds.top - containerBounds.top,
        });
    }

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Versions & Licenses')}</h2>
                <p>{t('Current application version, selected dependency versions, and data licenses.')}</p>
            </section>

            <section className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                <div className="flex flex-col gap-4">
                    <article className="dashboard-panel items-stretch justify-start p-0">
                        <div className="flex flex-col gap-5 p-6 w-full h-full">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                                    <GitCommitHorizontal className="h-6 w-6" aria-hidden="true" />
                                </div>
                                <div className="space-y-1">
                                    <h3>{t('Application version')}</h3>
                                    <p>{t('Baseline release information')}</p>
                                </div>
                            </div>

                            <div className="flex justify-start">
                                <AppBrandV2 className="app-brand--showcase" />
                            </div>

                            <div className="stats border border-base-300 bg-base-100 shadow-sm">
                                <div className="stat">
                                    <div className="stat-title">{t('Version')} (package.json)</div>
                                    <div className="stat-value text-3xl">{APPLICATION_VERSION}</div>
                                    <div className="stat-desc">
                                        {t('Updated at {date}', {
                                            date: new Date(APPLICATION_VERSION_MODIFIED_AT).toLocaleString(),
                                        })}
                                    </div>
                                    <div className="mt-3 border-t border-base-300 pt-3 text-xs text-base-content/60">
                                        <a
                                            href="https://www.ea-energie.de/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-inherit no-underline hover:underline"
                                        >
                                            {CONTACT_DETAILS.copyright} {CONTACT_DETAILS.company}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </article>

                    <article className="dashboard-panel items-stretch justify-start p-0">
                        <div className="flex flex-col gap-5 p-6 w-full h-full">
                            <div className="flex items-start gap-4">
                                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-success">
                                    <Database className="h-6 w-6" aria-hidden="true" />
                                </div>
                                <div className="space-y-1">
                                    <h3>{t('Data licenses')}</h3>
                                    <p>{t('This project uses open-data datasets. The respective data sources and license conditions are listed below.')}</p>
                                </div>
                            </div>

                            <div className="flex flex-col gap-4">
                                {OPEN_DATA_LICENSE_ENTRIES.map((entry) => (
                                    <section key={entry.datasetUrl} className="rounded-box border border-base-300 bg-base-100 p-4">
                                        <h4 className="font-semibold">{t(entry.title)}</h4>
                                        <dl className="mt-4 grid gap-3 text-sm">
                                            <div>
                                                <dt className="font-medium text-base-content/70">{t('Data source')}</dt>
                                                <dd>{t(entry.dataSource)}</dd>
                                            </div>
                                            <div>
                                                <dt className="font-medium text-base-content/70">{t('Dataset')}</dt>
                                                <dd className="break-all">
                                                    <a
                                                        href={entry.datasetUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="link link-primary"
                                                    >
                                                        {entry.datasetUrl}
                                                    </a>
                                                </dd>
                                            </div>
                                            <div>
                                                <dt className="font-medium text-base-content/70">{t('License')}</dt>
                                                <dd className="flex flex-col gap-1">
                                                    <span>{t(entry.licenseTitle)}</span>
                                                    <a
                                                        href={entry.licenseUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="link link-primary break-all"
                                                    >
                                                        {entry.licenseUrl}
                                                    </a>
                                                </dd>
                                            </div>
                                            <div>
                                                <dt className="font-medium text-base-content/70">{t('Note')}</dt>
                                                <dd>{t(entry.note)}</dd>
                                            </div>
                                        </dl>
                                    </section>
                                ))}
                            </div>
                        </div>
                    </article>
                </div>

                <article className="dashboard-panel items-stretch justify-start p-0">
                    <div className="flex flex-col gap-5 p-6 w-full h-full">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-secondary">
                                <Package className="h-6 w-6" aria-hidden="true" />
                            </div>
                            <div className="space-y-1">
                                <h3>{t('Core dependencies')}</h3>
                                <p>{t('Selected package versions relevant to the current UI stack')}</p>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="table table-zebra">
                                <thead>
                                    <tr>
                                        <th>{t('Dependency')}</th>
                                        <th>{t('Version')}</th>
                                        <th>{t('License')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {IMPORTANT_VERSION_ENTRIES.map((entry) => (
                                        <tr key={entry.name}>
                                            <td className="font-medium">{entry.name}</td>
                                            <td>{entry.version}</td>
                                            <td>{entry.license}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </article>
            </section>

            {hasSession && !IS_DEMO_USER ? (
                <section className="dashboard-panel items-stretch justify-start p-0">
                    <div className="flex flex-col gap-5 p-6 w-full h-full">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-accent">
                                <Wrench className="h-5 w-5" aria-hidden="true" />
                            </div>
                            <div className="flex w-full flex-wrap items-start justify-between gap-4">
                                <div className="space-y-1">
                                    <h3>{t('Version history')}</h3>
                                    <p>{t('Rendered directly from the project CHANGELOG.md file.')}</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    <select
                                        className="select select-bordered select-sm w-auto"
                                        aria-label={t('Show version')}
                                        defaultValue=""
                                        onChange={(event) => {
                                            scrollToChangelogVersion(event.target.value);
                                        }}
                                    >
                                        <option value="">{t('Show version')}</option>
                                        {changelogVersions.map((version) => (
                                            <option key={version.value} value={version.value}>
                                                {version.heading}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <StaticMarkdown
                            className="markdown-surface--scroll-500"
                            content={APPLICATION_CHANGELOG}
                            containerRef={changelogContainerRef}
                        />
                    </div>
                </section>
            ) : null}
        </div>
    );
}

export default VersionsTab;
